import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  X,
  Phone,
  Users,
  CheckSquare,
  Flag,
  Mail,
  Utensils,
} from "lucide-react";
import { useAppSelector } from "hooks";
import { ProjectCardData } from "containers/project-board/types";
import { DueDateBadge } from "./CardBadges";
import {
  fetchProjectActivities,
  fetchScheduledProjectActivities,
  patchProjectActivity,
  ProjectActivity,
  ScheduledProjectActivity,
  ProjectActivityType,
} from "services/project-activities.service";
import { formatActivityWhen } from "components/project/project-activity-details/project-activity-details";
import ProjectActivityDoneToggle from "components/project/project-activity-done-toggle";
import { toast } from "sonner";

export type DueBucket = "overdue" | "today" | "upcoming";
export type AgendaSection = "activities" | "due_dates";

const ACTIVITY_ICONS: Record<
  ProjectActivityType,
  typeof Phone
> = {
  CALL: Phone,
  MEETING: Users,
  TASK: CheckSquare,
  DEADLINE: Flag,
  EMAIL: Mail,
  LUNCH: Utensils,
};

function localYmd(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dueDayKey(dueDate: string): string | null {
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return null;
  return localYmd(d);
}

function bucketFor(dueKey: string, todayKey: string): DueBucket {
  if (dueKey < todayKey) return "overdue";
  if (dueKey === todayKey) return "today";
  return "upcoming";
}

function sortByDueAsc(a: ProjectCardData, b: ProjectCardData): number {
  const ak = a.dueDate ? dueDayKey(a.dueDate) || "" : "";
  const bk = b.dueDate ? dueDayKey(b.dueDate) || "" : "";
  if (ak !== bk) return ak.localeCompare(bk);
  return (a.name || "").localeCompare(b.name || "");
}

function activityScheduleAt(activity: ProjectActivity): number | null {
  if (activity.startAt) return activity.startAt;
  if (activity.createdAt) return activity.createdAt;
  return null;
}

function activityBucketFor(at: number, todayKey: string): DueBucket {
  const key = localYmd(new Date(at));
  return bucketFor(key, todayKey);
}

function buildScheduledRow(
  row: ProjectActivity,
  card: ProjectCardData,
  columnName: string | null,
  boardId: string,
  todayKey: string,
  now: number
): ScheduledProjectActivity {
  const at = activityScheduleAt(row)!;
  const bucket = activityBucketFor(at, todayKey);
  return {
    ...row,
    bucket,
    isOverdue: at < now && !row.isDone,
    projectName: card.name,
    projectOutcome: card.outcome || null,
    boardId,
    boardName: null,
    columnName,
    contactPhone: card.contactPhone ?? null,
    contactEmail: card.contactEmail ?? null,
    contactName: card.contactName ?? null,
  };
}

async function loadBoardActivitiesFromCards(
  companyId: string,
  boardId: string,
  cards: ProjectCardData[],
  columnsById: Record<string, { name?: string } | undefined>
): Promise<ScheduledProjectActivity[]> {
  const todayKey = localYmd(new Date());
  const now = Date.now();
  // Cap parallel fetches — large boards still cover Won/Lost/Deleted cards.
  const BATCH = 25;
  const chunks: Array<{ card: ProjectCardData; rows: ProjectActivity[] }> = [];
  for (let i = 0; i < cards.length; i += BATCH) {
    const slice = cards.slice(i, i + BATCH);
    const batch = await Promise.all(
      slice.map(async (card) => {
        try {
          const rows = await fetchProjectActivities(companyId, card.id);
          return { card, rows: Array.isArray(rows) ? rows : [] };
        } catch {
          return { card, rows: [] as ProjectActivity[] };
        }
      })
    );
    chunks.push(...batch);
  }

  const list: ScheduledProjectActivity[] = [];
  chunks.forEach(({ card, rows }) => {
    const columnName = columnsById[card.projectBoardColumn]?.name || null;
    rows.forEach((row) => {
      if (row.isDone) return;
      const at = activityScheduleAt(row);
      if (at === null) return;
      list.push(
        buildScheduledRow(row, card, columnName, boardId, todayKey, now)
      );
    });
  });
  list.sort(sortActivities);
  return list;
}

function applyActivityCounts(list: ScheduledProjectActivity[]) {
  const counts = { overdue: 0, today: 0, upcoming: 0 };
  list.forEach((row) => {
    if (row.bucket === "overdue") counts.overdue += 1;
    else if (row.bucket === "today") counts.today += 1;
    else counts.upcoming += 1;
  });
  return counts;
}

function pickDefaultTab(counts: {
  overdue: number;
  today: number;
  upcoming: number;
}): DueBucket {
  if (counts.today > 0) return "today";
  if (counts.upcoming > 0) return "upcoming";
  if (counts.overdue > 0) return "overdue";
  return "today";
}
function sortActivities(
  a: ScheduledProjectActivity,
  b: ScheduledProjectActivity
): number {
  const ak = a.startAt || 0;
  const bk = b.startAt || 0;
  if (ak !== bk) return ak - bk;
  return (a.title || "").localeCompare(b.title || "");
}

interface BoardAgendaPanelProps {
  open: boolean;
  onClose: () => void;
  onOpenCard: (cardId: string) => void;
  companyId: string;
  boardId: string;
}

export default function BoardAgendaPanel({
  open,
  onClose,
  onOpenCard,
  companyId,
  boardId,
}: BoardAgendaPanelProps) {
  const { t } = useTranslation();
  const [section, setSection] = useState<AgendaSection>("activities");
  const [tab, setTab] = useState<DueBucket>("today");
  const [activities, setActivities] = useState<ScheduledProjectActivity[]>([]);
  const [activityCounts, setActivityCounts] = useState({
    overdue: 0,
    today: 0,
    upcoming: 0,
  });
  const [loadingActivities, setLoadingActivities] = useState(false);

  const cardsById = useAppSelector((s) => s.projectBoardReducer.cardsById);
  const columnsById = useAppSelector((s) => s.projectBoardReducer.columnsById);

  /** All board cards (open + Won/Lost/Deleted) — pending dates still matter. */
  const boardCards = useMemo(
    () =>
      Object.values(cardsById || {}).filter(
        (card): card is ProjectCardData => !!card && !card.__optimistic
      ),
    [cardsById]
  );

  const tabInitializedRef = useRef(false);

  const loadActivities = useCallback(async () => {
    if (!companyId) return;
    setLoadingActivities(true);
    try {
      // Prefer company agenda API (includes closed deals on this board).
      // Fall back to per-card fetches only when that API is missing or fails —
      // an empty entries list is a valid calendar, not a signal to refetch.
      let list: ScheduledProjectActivity[] | null = null;
      try {
        const data = await fetchScheduledProjectActivities(companyId, {
          boardId: boardId || undefined,
        });
        if (Array.isArray(data.entries)) {
          list = data.entries;
        }
      } catch {
        list = null;
      }

      if (list === null && boardCards.length > 0) {
        list = await loadBoardActivitiesFromCards(
          companyId,
          boardId,
          boardCards,
          columnsById
        );
      }

      const next = list || [];
      setActivities(next);
      const counts = applyActivityCounts(next);
      setActivityCounts(counts);
      if (!tabInitializedRef.current) {
        setTab(pickDefaultTab(counts));
        tabInitializedRef.current = true;
      }
    } catch {
      toast.error(
        t("project_agenda_activities_load_failed", {
          defaultValue: "Could not load scheduled activities.",
        })
      );
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, [companyId, boardId, boardCards, columnsById, t]);

  useEffect(() => {
    if (!open) {
      tabInitializedRef.current = false;
      return;
    }
    loadActivities();
  }, [open, loadActivities]);

  const dueBuckets = useMemo(() => {
    const todayKey = localYmd(new Date());
    const overdue: ProjectCardData[] = [];
    const today: ProjectCardData[] = [];
    const upcoming: ProjectCardData[] = [];

    Object.values(cardsById || {}).forEach((card) => {
      // Include Won/Lost/Deleted — any pending due date still shows in Agenda.
      if (!card || card.__optimistic || !card.dueDate) {
        return;
      }
      const key = dueDayKey(card.dueDate);
      if (!key) return;
      const bucket = bucketFor(key, todayKey);
      if (bucket === "overdue") overdue.push(card);
      else if (bucket === "today") today.push(card);
      else upcoming.push(card);
    });

    overdue.sort(sortByDueAsc);
    today.sort(sortByDueAsc);
    upcoming.sort(sortByDueAsc);
    return { overdue, today, upcoming };
  }, [cardsById]);

  const activityBuckets = useMemo(() => {
    const overdue: ScheduledProjectActivity[] = [];
    const today: ScheduledProjectActivity[] = [];
    const upcoming: ScheduledProjectActivity[] = [];

    activities.forEach((row) => {
      if (row.bucket === "overdue") overdue.push(row);
      else if (row.bucket === "today") today.push(row);
      else upcoming.push(row);
    });

    overdue.sort(sortActivities);
    today.sort(sortActivities);
    upcoming.sort(sortActivities);
    return { overdue, today, upcoming };
  }, [activities]);

  const toggleActivityDone = async (row: ScheduledProjectActivity) => {
    try {
      await patchProjectActivity(companyId, row.project, row.id, {
        isDone: !row.isDone,
      });
      await loadActivities();
    } catch {
      toast.error(
        t("project_activity_save_failed", {
          defaultValue: "Could not update activity.",
        })
      );
    }
  };

  if (!open) return null;

  const bucketTabs = (counts: Record<DueBucket, number>) =>
    [
      {
        id: "overdue" as const,
        label: t("project_agenda_overdue", { defaultValue: "Overdue" }),
        count: counts.overdue,
      },
      {
        id: "today" as const,
        label: t("project_agenda_today", { defaultValue: "Due today" }),
        count: counts.today,
      },
      {
        id: "upcoming" as const,
        label: t("project_agenda_upcoming", { defaultValue: "Upcoming" }),
        count: counts.upcoming,
      },
    ] as const;

  const activityList = activityBuckets[tab];
  const dueList = dueBuckets[tab];
  const activeCounts =
    section === "activities"
      ? activityCounts
      : {
          overdue: dueBuckets.overdue.length,
          today: dueBuckets.today.length,
          upcoming: dueBuckets.upcoming.length,
        };

  return (
    <div
      className="absolute inset-0 z-30 flex justify-end bg-black/20"
      role="dialog"
      aria-modal="true"
      aria-label={t("project_agenda", { defaultValue: "Agenda" })}
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-md flex-col border-l border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[var(--p-color-border)] px-4 py-3">
          <CalendarClock size={18} strokeWidth={1.75} />
          <h2 className="flex-1 text-base font-semibold text-[var(--p-color-text)]">
            {t("project_agenda", { defaultValue: "Agenda" })}
          </h2>
          {/* On a phone the panel fills the width and has no dimmed area to tap, so the close button comes first, big, at the left. */}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)] max-md:order-first max-md:-my-2 max-md:-ml-2 max-md:p-3.5"
            aria-label={t("close", { defaultValue: "Close" })}
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex gap-1 border-b border-[var(--p-color-border)] px-3 py-2">
          {(
            [
              {
                id: "activities" as const,
                label: t("project_agenda_activities", {
                  defaultValue: "Activities",
                }),
              },
              {
                id: "due_dates" as const,
                label: t("project_agenda_due_dates", {
                  defaultValue: "Due dates",
                }),
              },
            ] as const
          ).map((item) => {
            const active = section === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setSection(item.id);
                  setTab(item.id === "activities" ? "today" : "overdue");
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold max-md:min-h-11 ${
                  active
                    ? "bg-[var(--p-color-bg-fill-brand)] text-[var(--p-color-text-brand-on-bg-fill)]"
                    : "text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)]"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex gap-1 border-b border-[var(--p-color-border)] px-3 py-2">
          {bucketTabs(activeCounts).map((item) => {
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold max-md:min-h-11 ${
                  active
                    ? item.id === "overdue"
                      ? "bg-[var(--p-color-bg-fill-critical)] text-[var(--p-color-text-critical-on-bg-fill)]"
                      : "bg-[var(--p-color-bg-fill-brand)] text-[var(--p-color-text-brand-on-bg-fill)]"
                    : "text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)]"
                }`}
              >
                {item.label}
                <span className="ml-1 opacity-80">({item.count})</span>
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {section === "activities" ? (
            loadingActivities ? (
              <p className="px-1 py-6 text-center text-sm text-[var(--p-color-text-secondary)]">
                {t("loading", { defaultValue: "Loading…" })}
              </p>
            ) : activityList.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-[var(--p-color-text-secondary)]">
                {t("project_agenda_activities_empty", {
                  defaultValue:
                    "No scheduled calls, emails, or tasks in this list.",
                })}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {activityList.map((row) => {
                  const Icon = ACTIVITY_ICONS[row.activityType] || CheckSquare;
                  const typeLabel = t(
                    `project_activity_${row.activityType.toLowerCase()}`,
                    {
                      defaultValue: row.activityType,
                    }
                  );
                  return (
                    <li key={row.id}>
                      <div
                        className={`flex gap-2 rounded-md border px-3 py-2.5 ${
                          row.isOverdue
                            ? "border-[var(--p-color-border-critical)] bg-[var(--p-color-bg-surface-critical)]"
                            : "border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)]"
                        }`}
                      >
                        <ProjectActivityDoneToggle
                          done={row.isDone}
                          onToggle={() => toggleActivityDone(row)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            onOpenCard(row.project);
                            onClose();
                          }}
                          className="min-w-0 flex-1 text-left hover:opacity-90"
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 inline-flex shrink-0 rounded bg-[var(--p-color-bg-surface-secondary)] p-1 text-[var(--p-color-text-secondary)]">
                              <Icon size={14} strokeWidth={1.75} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-[var(--p-color-text)]">
                                {row.title}
                              </div>
                              <div className="mt-0.5 text-xs text-[var(--p-color-text-secondary)]">
                                {typeLabel} ·{" "}
                                {formatActivityWhen(row.startAt, row.endAt)}
                              </div>
                              <div className="mt-1 text-xs text-[var(--p-color-text-secondary)]">
                                {row.projectName}
                                {row.projectOutcome && (
                                  <span className="ml-1 rounded bg-[var(--p-color-bg-surface-secondary)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--p-color-text)]">
                                    {row.projectOutcome}
                                  </span>
                                )}
                                {row.columnName && ` · ${row.columnName}`}
                                {row.assignedTo?.name &&
                                  ` · ${row.assignedTo.name}`}
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                      {row.activityType === "CALL" && row.contactPhone && (
                        <a
                          href={`tel:${row.contactPhone}`}
                          className="mt-1 ml-9 inline-flex text-xs font-semibold text-[var(--p-color-text-link)]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t("project_activity_call_now", {
                            defaultValue: "Call {{phone}}",
                            phone: row.contactPhone,
                          })}
                        </a>
                      )}
                      {row.activityType === "EMAIL" && row.contactEmail && (
                        <a
                          href={`mailto:${row.contactEmail}`}
                          className="mt-1 ml-9 inline-flex text-xs font-semibold text-[var(--p-color-text-link)]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t("project_activity_email_now", {
                            defaultValue: "Email {{email}}",
                            email: row.contactEmail,
                          })}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          ) : dueList.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-[var(--p-color-text-secondary)]">
              {t("project_agenda_empty", {
                defaultValue: "No open cards in this list.",
              })}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {dueList.map((card) => {
                const column = columnsById[card.projectBoardColumn];
                return (
                  <li key={card.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onOpenCard(card.id);
                        onClose();
                      }}
                      className="flex w-full flex-col gap-1 rounded-md border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2.5 text-left hover:bg-[var(--p-color-bg-surface-secondary)]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-semibold text-[var(--p-color-text)]">
                          {card.name}
                        </span>
                        <DueDateBadge card={card} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--p-color-text-secondary)]">
                        {card.outcome && (
                          <span className="rounded bg-[var(--p-color-bg-surface-secondary)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--p-color-text)]">
                            {card.outcome}
                          </span>
                        )}
                        {column?.name && <span>{column.name}</span>}
                        {card.customer?.name && (
                          <span>· {card.customer.name}</span>
                        )}
                        {card.user?.name && <span>· {card.user.name}</span>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="border-t border-[var(--p-color-border)] px-4 py-2 text-[11px] text-[var(--p-color-text-secondary)]">
          {section === "activities"
            ? t("project_agenda_activities_hint", {
                defaultValue:
                  "Pending scheduled calls, emails, meetings, and tasks — including Won / Lost / Deleted deals. Mark done from here or open the deal.",
              })
            : t("project_agenda_hint", {
                defaultValue:
                  "Cards with a due date, including Won / Lost / Deleted. Done activities are hidden.",
              })}
        </p>
      </div>
    </div>
  );
}
